import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Link2, Plus, RefreshCcw, Send, Trash2, XCircle } from 'lucide-react';
import { getTodayDate } from '../utils/dateUtils';
import { calendarsFor } from '../utils/businessDate';
import { resolveSettleDate, settleDateAdjustment } from '../utils/settlement';
import { parseAmountShorthand, formatAmountWithCommas } from '../utils/amountUtils';
import { useAuth } from '../contexts/AuthContext';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  formatCurrencyValue,
} from './tickets/shared/StructuredCreditTicketLayout';

type MultiLegType = 'ROLL' | 'SWAP';
type LegType = 'BUY' | 'SELL';

interface PortfolioOption {
  id: number;
  portfolioName: string;
}

interface SecurityHit {
  id: number;
  cusip?: string;
  securityName?: string;
  /** From the security master; picks the calendars the leg is dated on (ADR 0009). */
  currency?: string;
  securityType?: string;
}

interface MultiLegFormLeg {
  transactionType: LegType;
  /** What the operator typed (CUSIP / ticker prefix) */
  securityQuery: string;
  /** Resolved security from the typeahead; null until picked */
  security: SecurityHit | null;
  amount: string;
  price: string;
}

interface GroupLeg {
  id: number;
  transactionType: string;
  cusip?: string;
  securityName?: string;
  quantity?: number;
  price?: number;
  status?: string;
  netAmount?: number;
}

interface BlockedLeg {
  legId: number;
  errors: string[];
  overrideEligible?: boolean;
}

interface GroupView {
  legGroupRef: string;
  legCount: number;
  status: string;
  atomic: boolean;
  legs: GroupLeg[];
  blockedLegs?: BlockedLeg[];
}

const Shell = styled.div`
  min-width: 0;
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

const ModeButton = styled.button<{ $active?: boolean }>`
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${p => p.$active ? 'rgba(56, 189, 248, 0.48)' : 'rgba(71, 85, 105, 0.42)'};
  background: ${p => p.$active ? 'rgba(56, 189, 248, 0.14)' : 'rgba(15, 23, 42, 0.58)'};
  color: ${p => p.$active ? '#7dd3fc' : '#cbd5e1'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 7px;
`;

const Hint = styled.div`
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.74);
  border: 1px solid rgba(71, 85, 105, 0.52);
  color: #f8fafc;
  padding: 11px 12px;
  border-radius: 10px;
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.74);
  border: 1px solid rgba(71, 85, 105, 0.52);
  color: #f8fafc;
  padding: 11px 12px;
  border-radius: 10px;
  font-size: 14px;
`;

const TypeaheadWrap = styled.div`
  position: relative;
`;

const TypeaheadList = styled.ul`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.6);
  background: rgba(2, 6, 23, 0.98);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
`;

const TypeaheadItem = styled.li`
  padding: 9px 12px;
  cursor: pointer;
  color: #e2e8f0;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  gap: 12px;

  &:hover {
    background: rgba(56, 189, 248, 0.12);
  }

  span:last-child {
    color: #94a3b8;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
`;

const LegPanel = styled.div<{ $type: LegType }>`
  padding: 16px;
  border-radius: 14px;
  border: 1px solid ${p => p.$type === 'BUY' ? 'rgba(34, 197, 94, 0.34)' : 'rgba(248, 113, 113, 0.34)'};
  background:
    radial-gradient(circle at 100% 0%, ${p => p.$type === 'BUY' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(248, 113, 113, 0.12)'} 0%, rgba(15, 23, 42, 0) 38%),
    rgba(15, 23, 42, 0.64);
  margin-bottom: 12px;
`;

const LegHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const LegTitle = styled.div<{ $type: LegType }>`
  color: ${p => p.$type === 'BUY' ? '#86efac' : '#fca5a5'};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.42);
  background: rgba(2, 6, 23, 0.28);
  color: #cbd5e1;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const SecondaryButton = styled.button`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid rgba(56, 189, 248, 0.34);
  background: rgba(56, 189, 248, 0.1);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(SecondaryButton)`
  border-color: rgba(248, 113, 113, 0.4);
  background: rgba(248, 113, 113, 0.1);
  color: #fca5a5;
`;

const SubmitButton = styled.button`
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid rgba(34, 197, 94, 0.44);
  background: rgba(34, 197, 94, 0.14);
  color: #86efac;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Notice = styled.div<{ $tone?: 'error' | 'success' | 'info' }>`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${p => p.$tone === 'error' ? 'rgba(248, 113, 113, 0.34)' : p.$tone === 'info' ? 'rgba(56, 189, 248, 0.34)' : 'rgba(34, 197, 94, 0.34)'};
  background: ${p => p.$tone === 'error' ? 'rgba(248, 113, 113, 0.1)' : p.$tone === 'info' ? 'rgba(56, 189, 248, 0.08)' : 'rgba(34, 197, 94, 0.1)'};
  color: ${p => p.$tone === 'error' ? '#fca5a5' : p.$tone === 'info' ? '#7dd3fc' : '#86efac'};
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const LegTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #e2e8f0;

  th, td {
    padding: 9px 10px;
    text-align: left;
    border-bottom: 1px solid rgba(71, 85, 105, 0.35);
    white-space: nowrap;
  }

  th {
    color: #94a3b8;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  td.num, th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const StatusPill = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  border: 1px solid ${p => p.$status === 'POSTED' ? 'rgba(34, 197, 94, 0.5)'
    : p.$status.startsWith('MIXED') || p.$status === 'VOIDED' || p.$status === 'PRECLEAR_REJECTED' ? 'rgba(248, 113, 113, 0.5)'
    : 'rgba(56, 189, 248, 0.45)'};
  color: ${p => p.$status === 'POSTED' ? '#86efac'
    : p.$status.startsWith('MIXED') || p.$status === 'VOIDED' || p.$status === 'PRECLEAR_REJECTED' ? '#fca5a5'
    : '#7dd3fc'};
`;

const extractRows = (value: any) => Array.isArray(value?.content) ? value.content : Array.isArray(value) ? value : [];

/**
 * Dates for one leg (ADR 0009 §4.2): traded on its security's calendars (SIFMA for a USD bond, NYSE
 * for an equity), settled T+2 (US equities T+1) as the server counts it.
 */
const legSettlementQuery = (security: SecurityHit | null) => {
  const currency = security?.currency || 'USD';
  const securityType = security?.securityType || null;
  const equity = /EQUITY|ETF|STOCK/i.test(securityType || '');
  return {
    tradeDate: getTodayDate(calendarsFor('PUBLIC_FI', currency, null, securityType)),
    ticketType: 'PUBLIC_FI',
    convention: equity ? 'T+1' : 'T+2',
    currency,
    securityType,
  };
};

/** Server messages arrive as `{error}`, `{message}`, or a bare string (F-BT-018). */
const serverMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.message) return `${fallback} (${error.message})`;
  return fallback;
};

const emptyLeg = (side: LegType): MultiLegFormLeg => ({
  transactionType: side,
  securityQuery: '',
  security: null,
  amount: '',
  price: '',
});

const MultiLegTradeTicket: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupRefParam = searchParams.get('group') || '';

  const [type, setType] = useState<MultiLegType>('ROLL');
  const [portfolioId, setPortfolioId] = useState('');
  const [portfolios, setPortfolios] = useState<PortfolioOption[]>([]);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [legs, setLegs] = useState<MultiLegFormLeg[]>([emptyLeg('SELL'), emptyLeg('BUY')]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success' | 'info'; message: string } | null>(null);

  // typeahead state: which leg is open + its hits
  const [openLeg, setOpenLeg] = useState<number | null>(null);
  const [hits, setHits] = useState<SecurityHit[]>([]);
  const searchTimer = useRef<number | null>(null);

  // group workspace
  const [group, setGroup] = useState<GroupView | null>(null);
  const [openRef, setOpenRef] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupNotice, setGroupNotice] = useState<{ tone: 'error' | 'success' | 'info'; message: string } | null>(null);
  const [executedBy, setExecutedBy] = useState('');
  const [approveNotes, setApproveNotes] = useState('');

  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(
      (res) => setPortfolios(extractRows(res.data)),
      () => setPortfolios([]),
    );
  }, []);

  useEffect(() => {
    if (!executedBy && user) setExecutedBy(user.fullName || user.username || '');
  }, [user, executedBy]);

  const openByRef = () => {
    const raw = openRef.trim();
    if (!raw) return;
    const ref = raw.startsWith('GRP-') || raw.startsWith('GROUP-') ? raw : `GRP-${raw}`;
    setSearchParams({ group: ref });
    setGroupNotice(null);
  };

  const loadGroup = useCallback(async (ref: string) => {
    if (!ref) { setGroup(null); return; }
    try {
      const res = await axios.get(`/api/trade-groups/${encodeURIComponent(ref)}`);
      setGroup(res.data);
    } catch (error: any) {
      setGroup(null);
      setGroupNotice({ tone: 'error', message: serverMessage(error, `Leg group ${ref} could not be loaded.`) });
    }
  }, []);

  useEffect(() => {
    loadGroup(groupRefParam);
  }, [groupRefParam, loadGroup]);

  const grossNotional = useMemo(
    () => legs.reduce((sum, leg) => sum + parseAmountShorthand(leg.amount), 0),
    [legs],
  );

  const selectedPortfolio = portfolios.find((p) => String(p.id) === portfolioId);

  const updateLeg = (index: number, patch: Partial<MultiLegFormLeg>) => {
    setLegs((current) => current.map((leg, idx) => idx === index ? { ...leg, ...patch } : leg));
  };

  const searchSecurities = (index: number, query: string) => {
    updateLeg(index, { securityQuery: query, security: null });
    setOpenLeg(index);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    const prefix = query.trim();
    if (prefix.length < 2) { setHits([]); return; }
    searchTimer.current = window.setTimeout(async () => {
      try {
        const res = await axios.get('/api/dimensions/securities/cusip/search', { params: { prefix } });
        setHits(extractRows(res.data).slice(0, 12).map((s: any) => ({
          id: s.id, cusip: s.cusip, securityName: s.securityName, currency: s.currency, securityType: s.securityType,
        })));
      } catch {
        setHits([]);
      }
    }, 200);
  };

  const pickSecurity = (index: number, hit: SecurityHit) => {
    updateLeg(index, { security: hit, securityQuery: `${hit.cusip || hit.id} — ${hit.securityName || ''}`.trim() });
    setOpenLeg(null);
    setHits([]);
  };

  const addLeg = () => {
    setLegs((current) => [...current, emptyLeg(current.length % 2 === 0 ? 'SELL' : 'BUY')]);
  };

  const removeLeg = (index: number) => {
    setLegs((current) => current.length <= 2 ? current : current.filter((_, idx) => idx !== index));
  };

  const validate = () => {
    if (!portfolioId) return 'Select a portfolio before submitting the multi-leg ticket.';
    if (!reference.trim()) return 'Enter a desk reference for the package (it becomes the leg group id).';
    const unresolved = legs.findIndex((leg) => !leg.security);
    if (unresolved >= 0) return `Leg ${unresolved + 1}: pick a security from the CUSIP lookup.`;
    const badLeg = legs.findIndex((leg) => parseAmountShorthand(leg.amount) <= 0 || Number(leg.price) <= 0);
    if (badLeg >= 0) return `Leg ${badLeg + 1}: amount and execution price must be positive (amounts accept 500M = 500,000 and 1MM = 1,000,000).`;
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setNotice({ tone: 'error', message: validationError });
      return;
    }

    setLoading(true);
    setNotice(null);
    try {
      // The settle date the server will store for each leg (it falls back to the browser's count).
      const legDates = await Promise.all(legs.map(async (leg) => {
        const query = legSettlementQuery(leg.security);
        return { tradeDate: query.tradeDate, settleDate: await resolveSettleDate(query) };
      }));
      const payload = {
        type,
        name: `${type} ${reference.trim()}`,
        reference: reference.trim(),
        notes: notes.trim() || null,
        legs: legs.map((leg, index) => ({
          transactionType: leg.transactionType,
          securityId: leg.security!.id,
          cusip: leg.security!.cusip,
          amount: parseAmountShorthand(leg.amount),
          price: Number(leg.price),
          tradeDate: legDates[index].tradeDate,
          settleDate: legDates[index].settleDate,
          portfolioId: Number(portfolioId),
        })),
      };
      const res = await axios.post('/api/trade-transactions/multi-leg', payload);
      const created = extractRows(res.data);
      const ref = created[0]?.legGroupRef;
      // Legs come back in the order sent; say so when the server stored a different settle date.
      const moved = created
        .map((stored: any, index: number) => {
          const note = settleDateAdjustment(payload.legs[index]?.settleDate, stored?.settleDate);
          return note ? `Leg ${index + 1}: ${note}` : null;
        })
        .filter(Boolean);
      setNotice({
        tone: 'success',
        message: `Multi-leg ticket submitted: ${created.length} legs created as DRAFT under ${ref || 'a leg group'} (trades ${created.map((l: any) => l.id).join(', ')}). Advance them together below.`
          + (moved.length ? `\n${moved.join('\n')}` : ''),
      });
      if (ref) {
        setSearchParams({ group: ref });
      }
    } catch (error: any) {
      setNotice({ tone: 'error', message: serverMessage(error, 'Multi-leg submission failed.') });
    } finally {
      setLoading(false);
    }
  };

  const groupAction = async (action: string, body?: any, confirmText?: string) => {
    if (!group) return;
    if (confirmText && !window.confirm(confirmText)) return;
    setGroupBusy(true);
    setGroupNotice(null);
    try {
      const res = await axios.post(`/api/trade-groups/${encodeURIComponent(group.legGroupRef)}/${action}`, body || {});
      setGroup(res.data);
      const blocked: BlockedLeg[] = res.data?.blockedLegs || [];
      if (blocked.length > 0) {
        setGroupNotice({
          tone: 'error',
          message: blocked.map((b) => `Leg #${b.legId} blocked at pre-clear: ${b.errors.join('; ')}`).join('\n')
            + '\nThe other legs were submitted. Request a CCO override for each blocked leg below, or void the group.',
        });
      } else {
        setGroupNotice({ tone: 'success', message: `Group ${action} succeeded — status ${res.data?.status}.` });
      }
    } catch (error: any) {
      setGroupNotice({ tone: 'error', message: serverMessage(error, `Group ${action} failed.`) });
      await loadGroup(group.legGroupRef);
    } finally {
      setGroupBusy(false);
    }
  };

  const requestOverride = async (legId: number) => {
    if (!group) return;
    const justification = window.prompt(`Business justification for the CCO override on leg #${legId}:`, '');
    if (justification === null) return;
    if (!justification.trim()) { setGroupNotice({ tone: 'error', message: 'A business justification is required.' }); return; }
    setGroupBusy(true);
    try {
      await axios.post(`/api/pre-clear/${legId}/request-override`, { justification: justification.trim() });
      setGroupNotice({ tone: 'info', message: `Override requested for leg #${legId} — awaiting CCO in Compliance › Override Workflow. Once approved, the leg moves to PRECLEAR_APPROVED and the group can be sent.` });
      await loadGroup(group.legGroupRef);
    } catch (error: any) {
      setGroupNotice({ tone: 'error', message: serverMessage(error, `Override request for leg #${legId} failed.`) });
    } finally {
      setGroupBusy(false);
    }
  };

  const gs = group?.status || '';
  const canPreClear = gs === 'DRAFT' || gs === 'READY' || (gs.startsWith('MIXED(') && gs.includes('DRAFT') && !gs.includes('SENT') && !gs.includes('POSTED'));
  const canApprove = gs === 'PRECLEAR_PENDING' || (gs.startsWith('MIXED(') && gs.includes('PRECLEAR_PENDING'));
  const canSend = gs === 'DRAFT' || gs === 'PRECLEAR_APPROVED' || (gs.startsWith('MIXED(') && !gs.includes('PRECLEAR_PENDING') && !gs.includes('SENT') && !gs.includes('POSTED') && !gs.includes('EXECUTED'));
  const canExecute = gs === 'SENT';
  const canVoid = gs !== '' && !gs.includes('POSTED') && !gs.includes('EXECUTED') && !gs.includes('REVERSED') && gs !== 'VOIDED';
  const canReverse = gs === 'POSTED';

  return (
    <Shell>
      <StructuredWorkspace>
        <StructuredHeroPanel
          toneName="multi"
          icon={Link2}
          eyebrow="Atomic Multi-Leg"
          title={`${type === 'ROLL' ? 'Bond Roll' : 'Security Swap'} Ticket`}
          description="Stage linked buy and sell legs as one package, then pre-clear, send, execute and post them together — a leg that fails to post rolls the whole package back."
          badges={['Linked legs', 'Single portfolio target', 'Atomic execute + post']}
          signals={[
            { label: 'Portfolio', value: selectedPortfolio?.portfolioName || 'Select portfolio', accent: true },
            { label: 'Gross Notional', value: grossNotional > 0 ? formatCurrencyValue(grossNotional, 'USD') : '-', accent: true },
            { label: 'Legs', value: legs.length },
            { label: 'Mode', value: type },
          ]}
        />

        {!group && (
          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="multi">Work an Existing Package</BandEyebrow>
                <BandTitle>Open by Reference</BandTitle>
                <BandSubline>Browse packages in the Blotter (Packages view) or open one here by its reference.</BandSubline>
              </BandTitleGroup>
            </BandHeader>
            <FieldGrid>
              <div>
                <Label htmlFor="openPackageRef">Package reference</Label>
                <Input
                  id="openPackageRef"
                  value={openRef}
                  onChange={(e) => setOpenRef(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openByRef(); }}
                  placeholder="e.g. L4-ROLL-02"
                />
              </div>
              <div style={{ alignSelf: 'end' }}>
                <SecondaryButton type="button" onClick={openByRef} disabled={!openRef.trim()}>Open package</SecondaryButton>
              </div>
            </FieldGrid>
          </StructuredBand>
        )}

        {group && (
          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="multi">Leg Group</BandEyebrow>
                <BandTitle>{group.legGroupRef}</BandTitle>
                <BandSubline>
                  {group.legCount} legs · group status <StatusPill $status={group.status}>{group.status}</StatusPill>
                  {!group.atomic && ' — legs are out of step; the group refuses to advance until every leg allows it.'}
                </BandSubline>
              </BandTitleGroup>
              <ButtonRow style={{ marginTop: 0 }}>
                <SecondaryButton type="button" onClick={() => loadGroup(group.legGroupRef)} disabled={groupBusy}>
                  <RefreshCcw size={14} /> Refresh
                </SecondaryButton>
                <SecondaryButton type="button" onClick={() => { setSearchParams({}); setGroup(null); setGroupNotice(null); }} disabled={groupBusy}>
                  <XCircle size={14} /> Close package
                </SecondaryButton>
              </ButtonRow>
            </BandHeader>

            <div style={{ overflowX: 'auto' }}>
              <LegTable>
                <thead>
                  <tr>
                    <th>Trade</th><th>Side</th><th>Security</th><th className="num">Quantity</th><th className="num">Price</th><th className="num">Net</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {group.legs.map((leg) => (
                    <tr key={leg.id}>
                      <td>#{leg.id}</td>
                      <td>{leg.transactionType}</td>
                      <td>{leg.cusip || ''} {leg.securityName ? `— ${leg.securityName}` : ''}</td>
                      <td className="num">{formatAmountWithCommas(leg.quantity || 0)}</td>
                      <td className="num">{leg.price ?? ''}</td>
                      <td className="num">{leg.netAmount != null ? formatCurrencyValue(leg.netAmount, 'USD') : ''}</td>
                      <td><StatusPill $status={leg.status || ''}>{leg.status}</StatusPill></td>
                    </tr>
                  ))}
                </tbody>
              </LegTable>
            </div>

            <FieldGrid style={{ marginTop: 14 }}>
              <div>
                <Label htmlFor="groupExecutedBy">Executed By (for Execute)</Label>
                <Input id="groupExecutedBy" value={executedBy} onChange={(e) => setExecutedBy(e.target.value)} placeholder="Name of the executing trader" />
              </div>
              <div>
                <Label htmlFor="groupApproveNotes">Approval Notes (CCO)</Label>
                <Input id="groupApproveNotes" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} placeholder="Optional" />
              </div>
            </FieldGrid>

            <ButtonRow>
              <SecondaryButton type="button" disabled={groupBusy || !canPreClear} onClick={() => groupAction('pre-clear')}>
                <Send size={14} /> Submit Pre-Clear
              </SecondaryButton>
              <SecondaryButton type="button" disabled={groupBusy || !canApprove} onClick={() => groupAction('approve', { notes: approveNotes })}>
                <CheckCircle2 size={14} /> Approve (CCO)
              </SecondaryButton>
              <SecondaryButton type="button" disabled={groupBusy || !canSend} onClick={() => groupAction('send')}>
                <Send size={14} /> Send Group
              </SecondaryButton>
              <SubmitButton type="button" disabled={groupBusy || !canExecute || !executedBy.trim()} onClick={() => groupAction('execute', { executedBy: executedBy.trim() })}>
                <CheckCircle2 size={14} /> Execute + Post Group
              </SubmitButton>
              <DangerButton type="button" disabled={groupBusy || !canVoid} onClick={() => {
                const reason = window.prompt('Void reason (applies to every leg):', '') ?? null;
                if (reason === null) return;
                groupAction('void', { reason });
              }}>
                <XCircle size={14} /> Void Group
              </DangerButton>
              <DangerButton type="button" disabled={groupBusy || !canReverse} onClick={() => {
                const reason = window.prompt('Reversal reason (applies to every leg):', '') ?? null;
                if (reason === null) return;
                groupAction('reverse', { reason });
              }}>
                <RefreshCcw size={14} /> Reverse Group
              </DangerButton>
            </ButtonRow>

            {groupNotice && <div style={{ marginTop: 12 }}><Notice $tone={groupNotice.tone}>{groupNotice.message}</Notice></div>}
            {(group.blockedLegs || []).length > 0 && (
              <ButtonRow>
                {(group.blockedLegs || []).map((b) => (
                  <SecondaryButton key={b.legId} type="button" disabled={groupBusy} onClick={() => requestOverride(b.legId)}>
                    Request CCO Override for #{b.legId}
                  </SecondaryButton>
                ))}
              </ButtonRow>
            )}
          </StructuredBand>
        )}

        <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="multi">Ticket Mode</BandEyebrow>
              <BandTitle>Roll or Swap</BandTitle>
              <BandSubline>Choose whether the linked package represents a bond roll or a security swap.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <ModeGrid>
            <ModeButton type="button" $active={type === 'ROLL'} onClick={() => setType('ROLL')}>
              <Link2 size={16} />
              Bond Roll (Out / In)
            </ModeButton>
            <ModeButton type="button" $active={type === 'SWAP'} onClick={() => setType('SWAP')}>
              <Link2 size={16} />
              Security Swap
            </ModeButton>
          </ModeGrid>
        </StructuredBand>

        <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="multi">Portfolio Target</BandEyebrow>
              <BandTitle>Booking Portfolio and Reference</BandTitle>
              <BandSubline>All legs book into one portfolio. The desk reference becomes the leg group id and must be unique.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <FieldGrid>
            <div>
              <Label htmlFor="multiLegPortfolio">Portfolio*</Label>
              <Select id="multiLegPortfolio" value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)}>
                <option value="">Select Portfolio</option>
                {portfolios.map((portfolio) => (
                  <option key={portfolio.id} value={portfolio.id}>{portfolio.portfolioName}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="multiLegReference">Reference*</Label>
              <Input id="multiLegReference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. L4-ROLL-02" />
              <Hint>Becomes leg group GRP-&lt;reference&gt; on every leg.</Hint>
            </div>
            <div>
              <Label htmlFor="multiLegNotes">Notes</Label>
              <Input id="multiLegNotes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Rationale for the package" />
            </div>
          </FieldGrid>
        </StructuredBand>

        <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="multi">Leg Package</BandEyebrow>
              <BandTitle>Buy and Sell Legs</BandTitle>
              <BandSubline>Pick each leg’s security by CUSIP, then enter par (500M = 500,000; 1MM = 1,000,000) and execution price.</BandSubline>
            </BandTitleGroup>
          </BandHeader>

          {legs.map((leg, index) => (
            <LegPanel key={index} $type={leg.transactionType}>
              <LegHeader>
                <LegTitle $type={leg.transactionType}>Leg {index + 1}: {leg.transactionType}</LegTitle>
                <IconButton type="button" onClick={() => removeLeg(index)} disabled={legs.length <= 2} aria-label={`Remove leg ${index + 1}`}>
                  <Trash2 size={15} />
                </IconButton>
              </LegHeader>
              <FieldGrid>
                <div>
                  <Label>Side</Label>
                  <Select value={leg.transactionType} onChange={(e) => updateLeg(index, { transactionType: e.target.value as LegType })}>
                    <option value="SELL">Sell</option>
                    <option value="BUY">Buy</option>
                  </Select>
                </div>
                <TypeaheadWrap>
                  <Label>Security (CUSIP lookup)*</Label>
                  <Input
                    value={leg.securityQuery}
                    onChange={(e) => searchSecurities(index, e.target.value)}
                    onFocus={() => { if (!leg.security) setOpenLeg(index); }}
                    onBlur={() => window.setTimeout(() => setOpenLeg((cur) => (cur === index ? null : cur)), 150)}
                    placeholder="Type a CUSIP prefix, e.g. TDG"
                    autoComplete="off"
                  />
                  {openLeg === index && hits.length > 0 && !leg.security && (
                    <TypeaheadList>
                      {hits.map((hit) => (
                        <TypeaheadItem key={hit.id} onMouseDown={() => pickSecurity(index, hit)}>
                          <span>{hit.securityName || `Security ${hit.id}`}</span>
                          <span>{hit.cusip}</span>
                        </TypeaheadItem>
                      ))}
                    </TypeaheadList>
                  )}
                  {leg.security
                    ? <Hint>Resolved: id {leg.security.id} · {leg.security.cusip}</Hint>
                    : <Hint>Pick from the list — free text is not accepted.</Hint>}
                </TypeaheadWrap>
                <div>
                  <Label>Amount (Par)</Label>
                  <Input
                    type="text"
                    value={leg.amount}
                    onChange={(e) => updateLeg(index, { amount: e.target.value })}
                    onBlur={(e) => {
                      // Same behaviour as the single-leg tickets: expand 500M / 1MM in the field itself.
                      const parsed = parseAmountShorthand(e.target.value);
                      if (parsed > 0) updateLeg(index, { amount: formatAmountWithCommas(parsed) });
                    }}
                    placeholder="Enter par amount (e.g., 2MM)"
                  />
                </div>
                <div>
                  <Label>Execution Price</Label>
                  <Input inputMode="decimal" value={leg.price} onChange={(e) => updateLeg(index, { price: e.target.value })} placeholder="99.50" />
                </div>
              </FieldGrid>
            </LegPanel>
          ))}

          <ActionBar>
            <SecondaryButton type="button" onClick={addLeg}>
              <Plus size={16} />
              Add Leg
            </SecondaryButton>
            <SubmitButton type="button" onClick={handleSubmit} disabled={loading}>
              <Send size={16} />
              {loading ? 'Submitting...' : `Stage ${type} Package`}
            </SubmitButton>
          </ActionBar>
        </StructuredBand>

        {notice && <Notice $tone={notice.tone}>{notice.message}</Notice>}
      </StructuredWorkspace>
    </Shell>
  );
};

export default MultiLegTradeTicket;
